import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const BreadcrumbContext = createContext(null);

/**
 * Provee un diccionario mutable `{ [key]: label }` donde las páginas de
 * detalle pueden registrar el label real de un segmento dinámico del
 * breadcrumb (ej. nombre del cliente vs "Cliente #27").
 */
export function BreadcrumbProvider({ children }) {
  const [dynamicLabels, setDynamicLabels] = useState({});

  const setLabel = useCallback((key, label) => {
    setDynamicLabels((prev) => {
      if (prev[key] === label) return prev;
      return { ...prev, [key]: label };
    });
  }, []);

  const value = useMemo(() => ({ dynamicLabels, setLabel }), [dynamicLabels, setLabel]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbContext() {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    // Componente usado fuera del provider: devolvemos un fallback vacío
    // en vez de throw para que un mismo hook sea seguro en layouts que
    // no hayan montado el provider (ej. LayoutLogin).
    return { dynamicLabels: {}, setLabel: () => {} };
  }
  return ctx;
}

/**
 * Hook para que una página de detalle registre el label dinámico de su
 * breadcrumb. Llamar con una key estable (ej. `client:${id}`) y un label
 * que puede ser null mientras el fetch carga — los nulls no pisan.
 */
export function useSetBreadcrumbLabel(key, label) {
  const { setLabel } = useBreadcrumbContext();
  useEffect(() => {
    if (key && label) setLabel(key, label);
  }, [key, label, setLabel]);
}
